<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="iV Users" backTo="tools">
      <button
        @click="showingCreateEditEngineer = true"
        class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
          class="stroke-current h-5 w-5 mr-2"
          fill="none"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          viewBox="0 0 24 24"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>Create Engineer</span>
      </button>
    </page-header>

    <main class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md">
        <div class="w-full flex items-center justify-between mb-5">
          <div class="flex space-x-2">
            <button
              @click="selectTab('all')"
              class="px-4 py-2 rounded transition-colors duration-300 flex items-center font-semibold"
              :class="{
                'bg-orange-300 text-orange-900': currentTab == 'all',
                'hover:bg-gray-200': currentTab != 'all',
              }"
            >
              <svg
                class="stroke-current h-5 w-5 mr-2"
                fill="none"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                viewBox="0 0 24 24"
              >
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
              </svg>
              <span>All Engineers</span>
            </button>

            <button
              @click="currentTab = 'search'"
              class="px-4 py-2 rounded transition-colors duration-300 flex items-center font-semibold"
              :class="{
                'bg-orange-300 text-orange-900': currentTab == 'search',
                'hover:bg-gray-200': currentTab != 'search',
              }"
            >
              <svg
                class="stroke-current h-5 w-5 mr-2"
                fill="none"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <span>Search</span>
            </button>
          </div>
          <div class="flex flex-1 justify-end">
            <input
              type="text"
              class="ml-10 w-full lg:w-1/2 rounded-l bge-input bge-input-spacing"
              placeholder="Search engineers by name or agent"
              v-model="searchTerm"
              @keydown.enter="searchEngineers"
            />
            <button
              @click="searchEngineers"
              class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
            >
              <svg
                class="stroke-current h-5 w-5"
                fill="none"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            </button>
          </div>
        </div>
        <div v-if="viewState == 'Idle'">
          <Table
            v-if="currentTab == 'all'"
            :columns="columns"
            :data="engineers"
            :totalRecords="paginationOptions.totalRecords"
            :hasPagination="true"
            :currentPage="paginationOptions.currentPage"
            :totalPages="paginationOptions.totalPages"
            :initialSortOptions="sortOptions"
            @onSortChanged="onTableSortChanged"
            @onPageChanged="onTablePageChanged"
          >
            <template v-slot:name="props">
              <p>{{ `${props.row.firstname} ${props.row.lastname}` }}</p>
            </template>
            <template v-slot:access_levels="props">
              <div class="flex items-center space-x-2">
                <template v-for="level in props.row.sw900_access_levels">
                  <p
                    :key="level"
                    class="bg-gray-300 rounded leading-none px-2 py-1"
                  >
                    {{ level }}
                  </p>
                </template>
              </div>
            </template>
            <template v-slot:actions="props">
              <div class="flex items-center space-x-2">
                <button
                  @click="editEngineer(props.row)"
                  class="flex items-center bg-blue-200 border hover:bg-blue-300 border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md p-1"
                >
                  <svg
                    class="stroke-current h-5 w-5"
                    fill="none"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                    />
                  </svg>
                </button>
                <button
                  @click="deleteEngineer(props.row)"
                  class="flex items-center bg-red-200 border hover:bg-red-300 border-red-400 hover:border-red-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md p-1"
                >
                  <svg
                    class="stroke-current h-5 w-5"
                    fill="none"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"
                    />
                  </svg>
                </button>
              </div>
            </template>
          </Table>
          <Table
            v-if="currentTab == 'search'"
            :columns="columns"
            :data="searchResults"
            :totalRecords="searchPaginationOptions.totalRecords"
            :hasPagination="true"
            :currentPage="searchPaginationOptions.currentPage"
            :totalPages="searchPaginationOptions.totalPages"
            :initialSortOptions="searchSortOptions"
            @onSortChanged="onTableSortChanged"
            @onPageChanged="onTablePageChanged"
          >
            <template v-slot:name="props">
              <p>{{ `${props.row.firstname} ${props.row.lastname}` }}</p>
            </template>
            <template v-slot:access_levels="props">
              <div class="flex items-center space-x-2">
                <template v-for="level in props.row.sw900_access_levels">
                  <p
                    :key="level"
                    class="bg-gray-300 rounded leading-none px-2 py-1"
                  >
                    {{ level }}
                  </p>
                </template>
              </div>
            </template>
            <template v-slot:actions="props">
              <div class="flex items-center space-x-2">
                <button
                  @click="editEngineer(props.row)"
                  class="flex items-center bg-blue-200 border hover:bg-blue-300 border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md p-1"
                >
                  <svg
                    class="stroke-current h-5 w-5"
                    fill="none"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                    />
                  </svg>
                </button>
                <button
                  @click="deleteEngineer(props.row)"
                  class="flex items-center bg-red-200 border hover:bg-red-300 border-red-400 hover:border-red-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md p-1"
                >
                  <svg
                    class="stroke-current h-5 w-5"
                    fill="none"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"
                    />
                  </svg>
                </button>
              </div>
            </template>
          </Table>
        </div>
        <div v-show="viewState == 'Loading'">
          <spinner class="mx-auto my-4" :color="'text-black'" :size="8" />
        </div>

        <div v-if="error">
          <div
            class="bg-red-200 border border-red-500 rounded-lg py-4 px-5 text-red-900"
          >
            <p>Couldn't load engineers {{ error ? `(${error})` : `` }}</p>
          </div>
        </div>
      </div>
    </main>
    <portal to="overlay-outlet">
      <Panel
        :showing="showingCreateEditEngineer"
        @close="closePanel"
        :title="engineerToEdit.user_id ? 'Edit Engineer' : 'Create Engineer'"
      >
        <!-- <CreateIvyActivation @onActivationComplete="onActivationComplete" /> -->
        <CreateEditEngineer
          :engineer="engineerToEdit"
          @onSaveEngineer="onEngineerSaved"
        />
        <!-- @complete="" -->
      </Panel>
    </portal>
    
    <portal to="overlay-outlet">
      <confirm-alert
        :showingConfirm="confirmAlert.showing"
        :message="confirmAlert.message"
        @complete="confirmAlert.resultFunction"
      />
    </portal>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Table from "@/components/Table.vue";
import Panel from "@/components/Panel.vue";
import Spinner from "@/components/Spinner.vue";
import CreateEditEngineer from "@/components/Ivy/CreateEditEngineer.vue";

const ConfirmAlert = () => import("@/components/ConfirmAlert.vue");
const Notification = () => import("@/components/Notification.vue");

export default {
  name: "EngineerAccess",
  components: {
    PageHeader,
    Table,
    Panel,
    Spinner,
    CreateEditEngineer,
    ConfirmAlert,
    Notification,
  },
  data() {
    return {
      viewState: "Idle",
      currentTab: "all",
      searchTerm: "",
      searchTermWas: "",
      searchResults: [],
      showingCreateEditEngineer: false,
      engineerToEdit: {},
      engineers: [],
      error: null,
      columns: [
        { title: "Name", field: "name", type: "custom" },
        { title: "Agent", field: "org_name", selector: (row) => row.org_name },
        {
          title: "Access Levels",
          field: "access_levels",
          type: "custom",
        },
        {
          field: "actions",
          type: "custom",
          widthClass: "w-20",
          sortable: false,
        },
      ],
      paginationOptions: {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
      },
      sortOptions: {
        field: "org_name",
        isAsc: false,
        pageSize: 50,
      },
      searchPaginationOptions: {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
      },
      searchSortOptions: {
        field: "org_name",
        isAsc: false,
        pageSize: 50,
      },
      confirmAlert: {
        resultFunction: "",
        showing: false,
        message: "",
        title: "",
        data: "",
      },
    };
  },
  async mounted() {
    await this.getEngineers();
  },
  methods: {
    selectTab: function(tabName) {
      this.$nextTick(() => {
        this.currentTab = tabName;
      });
    },
    async searchEngineers() {
      if (!this.searchTerm || this.searchTerm.length == 0) return;

      try {
        this.viewState = "Loading";

        this.$nextTick(() => {
          this.currentTab = "search";
        });

        let response = await this.EngineerService.getEngineers({
          ...this.searchPaginationOptions,
          ...this.searchSortOptions,
          searchTerm: this.searchTerm,
        });
        
        this.searchResults = response.data.filter((eng) => {
          return eng.sw900_access;
        });
        this.searchPaginationOptions.currentPage = response.page;
        this.searchPaginationOptions.totalPages = response.page_count;
        this.searchPaginationOptions.totalRecords = response.count;
        this.searchTermWas = this.searchTerm;
        this.viewState = "Idle";
      } 
      catch (err) {
        console.error(err);
        this.viewState = "Error";
        this.error = err;
      }
    },
    getEngineers: async function() {
      this.viewState = "Loading";
      try {
        let result = await this.EngineerService.getEngineers({
          ...this.paginationOptions,
          ...this.sortOptions,
        });
        this.engineers = result.data.filter((eng) => {
          return eng.sw900_access;
        });
        this.paginationOptions.totalRecords = result.count;
        this.paginationOptions.totalPages = result.page_count;
        this.viewState = "Idle";
      } 
      catch (error) {
        console.error('Error getting engineers', error);
        this.viewState = "Error";
        this.error = error;
      }
    },
    onTableSortChanged: async function(sortOptions) {
      if (this.currentTab == "all") {
        this.sortOptions = sortOptions;
        await this.getEngineers();
      } 
      else {
        this.searchSortOptions = sortOptions;
        await this.searchEngineers();
      }
    },
    onTablePageChanged: async function(direction) {
      if (this.currentTab == "all") {
        this.paginationOptions.currentPage = this.paginationOptions.currentPage + direction;
        await this.getEngineers();
      } 
      else {
        this.searchPaginationOptions.currentPage = this.searchPaginationOptions.currentPage + direction;
        await this.searchEngineers();
      }
    },
    editEngineer: function(engineer) {
      this.engineerToEdit = engineer;
      this.showingCreateEditEngineer = true;
    },
    closePanel: function() {
      this.showingCreateEditEngineer = false;
      this.engineerToEdit = {};
    },
    onEngineerSaved: async function() {
      this.closePanel();
      await this.getEngineers();
    },
    deleteEngineer: function(engineer) {
      this.confirmAlert = {
        resultFunction: this.handleDeleteEngineer,
        message: `Are you sure you wish to delete '${engineer.firstname} ${engineer.lastname}'? This cannot be undone.`,
        showing: true,
        data: engineer,
      };
    },
    handleDeleteEngineer: async function(result) {
      if (result) {
        this.confirmAlert.showing = false;
        try {
          await this.EngineerService.deleteEngineer(this.confirmAlert.data.user_id);

          if (this.searchTerm != "") {
            await this.searchEngineers();
          } 
          else {
            await this.getEngineers();
          }
          
          this.$breadstick.notify(
            ({ h, onClose }) => {
              return h(
                Notification,
                {
                  props: {
                    title: "Engineer Deleted",
                    close: onClose,
                  },
                },
                `Engineer deleted`
              );
            },
            {
              position: "top-right",
            }
          );
          
          // Refresh the engineers list
          await this.getEngineers();
        } 
        catch (error) {
          console.error('Error deleting engineer', error);
          this.$breadstick.notify(
            ({ h, onClose }) => {
              return h(
                Notification,
                {
                  props: {
                    type: "danger",
                    title: "Error",
                    close: onClose,
                  },
                },
                "There was a problem deleting the engineer"
              );
            },
            {
              position: "top-right",
            }
          );
        }
      }
      
      this.confirmAlert = {
        resultFunction: "",
        message: "",
        showing: false,
        data: "",
      };
    },
  },
};
</script>
